import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type { ModalProps } from 'src/components/common/Modal';
import Modal from 'src/components/common/Modal';

const FILE_TYPES = ['XLSX', 'PPTX'];

interface FileTypeChooserModalProps extends ModalProps {
  onSelectFileType: () => Promise<void>;
  handlePPTModal?: () => void;
  note?: string;
}

const FileTypeChooserModal: FC<FileTypeChooserModalProps> = ({
  onSelectFileType,
  note,
  handlePPTModal,
  ...modalProps
}) => {
  const { t } = useTranslation(['common', 'planAction']);
  const [loading, setLoading] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState(FILE_TYPES[0]);

  const download = () => {
    if (selectedFileType === 'XLSX') {
      setLoading(true);
      onSelectFileType().finally(() => {
        setLoading(false);
        modalProps.onClose();
      });
    } else {
      modalProps.onClose();
      handlePPTModal?.();
    }
  };

  return (
    <Modal {...modalProps}>
      {!loading && <h4>{t('common:pleaseChooseFollowing')}</h4>}
      <Stack direction="column" spacing={3}>
        {loading ? (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              py: 5,
            }}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <InputLabel htmlFor="fileTypeSelectionField">
              <Typography color="initial">
                {t('planAction:SelectModal.FileType')}
              </Typography>
            </InputLabel>

            <Select
              size="small"
              id="fileTypeSelectionField"
              name="fileType"
              value={selectedFileType}
              onChange={(e) => setSelectedFileType(e.target.value)}
            >
              {FILE_TYPES.map((fileType) => (
                <MenuItem key={fileType} value={fileType}>
                  {fileType}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        {note && <p>{note}</p>}
        {!loading && (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Button variant="cta" onClick={download}>
              {t('download')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default FileTypeChooserModal;
